import { useRef, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { parseISOToDateTime, toTimezone } from '@calendly/datetime'
import { formatTime } from '@calendly/booking-kit/services/time_format'

import { timezoneSelector } from 'selectors'
import { currentUserSelector } from 'selectors/user'
import { inviteeEventHasConflictSelector } from 'selectors/mutual_availability'
import { useI18n } from 'locales'
import { BodyText } from '@calendly/ui/components/typography'

import {
  Wrapper,
  ConflictMarker,
  Name,
  Time,
  RedesignConflictBadge
} from './styles'

const formatTimeSpan = (startTime, endTime, timezone, timeNotation) => (
  `${formatTime(toTimezone(parseISOToDateTime(startTime), timezone), timeNotation)}
      - ${formatTime(toTimezone(parseISOToDateTime(endTime), timezone), timeNotation)}`
)

const InviteeEvent = ({
  id,
  startTime,
  endTime,
  eventName,
  containerScreenPosition,
  autoScroll,
  allDay,
  variant = 'default'
}) => {
  const { i18n } = useI18n()
  const ref = useRef()
  const timezone = useSelector(timezoneSelector)
  const currentUser = useSelector(currentUserSelector)
  const hasConflict = useSelector((state) => inviteeEventHasConflictSelector(state, { id }))
  const { top: containerTop, bottom: containerBottom } = containerScreenPosition

  useEffect(() => {
    if (!autoScroll) return
    const { top, bottom } = ref.current.getBoundingClientRect()
    if (top >= containerTop && bottom <= containerBottom) return

    ref.current.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    })
  }, [autoScroll, containerTop, containerBottom])

  const isRedesign = variant === 'redesign'
  const timeNotationForDisplay = currentUser.time_notation || '12h'

  let timeContent
  if (allDay) {
    timeContent = i18n.t('publisher.mutual_availability.all_day', { locale: currentUser.locale })
  } else {
    timeContent = formatTimeSpan(startTime, endTime, timezone, timeNotationForDisplay)
  }

  return (
    <Wrapper hasConflict={hasConflict} ref={ref} allDay={allDay} redesign={isRedesign}>
      {isRedesign ? (
        <>
          <div>
            <Name>{eventName}</Name>
            <Time>{timeContent}</Time>
          </div>
          {hasConflict && (
            <RedesignConflictBadge>
              <BodyText weight="bold" color="standard" inline>
                {i18n.t('publisher.mutual_availability.conflict', { locale: currentUser.locale })}
              </BodyText>
            </RedesignConflictBadge>
          )}
        </>
      ) : (
        <>
          <Time>{timeContent}</Time>
          <Name>{eventName}</Name>
          {hasConflict && (
            <ConflictMarker>
              {i18n.t('publisher.mutual_availability.conflict', { locale: currentUser.locale })}
            </ConflictMarker>
          )}
        </>
      )}
    </Wrapper>
  )
}

export default InviteeEvent
